import type { MeDTO } from '@/features/auths/schemas/get-me.schema';
import type { LoginPayload } from '@/features/auths/schemas/log-in.schema';
import { createContext, useContext } from 'react';

export interface AuthContext {
  /** Current user from get-me, or null when logged out or not yet resolved. */
  user: MeDTO | null;
  /** True while the session has a token and the get-me request is still pending. */
  isLoadingAuth: boolean;
  /** True when tokens exist, get-me has finished, and a user record is present. */
  isAuthenticated: boolean;
  /** True while the sign-in mutation is in flight. */
  isSigningIn: boolean;
  /** True while the sign-out mutation is in flight. */
  isSigningOut: boolean;
  /** True while the current user query is fetching. */
  isFetchUser: boolean;
  /** Runs sign-in; persists tokens and refreshes the current user on success. */
  signIn: (params: LoginPayload) => Promise<void>;
  /** Runs sign-out; clears tokens and user cache when settled. */
  signOut: () => Promise<void>;
  /** Refetches the current-user query (same as get-me). */
  refetchUser: () => Promise<unknown>;
}

export const authContext = createContext<AuthContext | null>(null);

export function useAuthContext(): AuthContext {
  const ctx = useContext(authContext);
  if (ctx == null) {
    throw new Error('useAuthContext must be used within <AuthProvider>.');
  }
  return ctx;
}
